import type { PanelUser } from '@/shared/types';

/** Só a lista importa aqui; a role entra apenas na checagem de acesso. */
type WithPassbooks = Pick<PanelUser, 'id_passbooks'>;
type WithRole = WithPassbooks & { role?: PanelUser['role'] };

/**
 * Lojas da conta, sempre como lista.
 *
 * O legado grava `id_passbooks` como array, mas o campo já chegou como string
 * em base antiga. Quem trata isso com `String(user.id_passbooks)` funciona por
 * acidente com uma loja e monta `"id1,id2"` com duas, que é id inválido na URL.
 */
export function passbookIdsOf(user?: WithPassbooks | null): string[] {
  const raw = user?.id_passbooks;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  const ids: string[] = [];
  for (const item of list) {
    const id = String(item ?? '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

export function firstPassbookId(user?: WithPassbooks | null): string | undefined {
  return passbookIdsOf(user)[0];
}

/** Conta de grupo: mais de uma marca no mesmo login. */
export function hasMultiplePassbooks(user?: WithPassbooks | null): boolean {
  return passbookIdsOf(user).length > 1;
}

/**
 * Admin e Owner inspecionam qualquer loja; o resto só as próprias.
 *
 * Conta sem vínculo nenhum não é bloqueada aqui: quem decide isso é a role,
 * em `canAccessTenantPath`.
 */
export function canAccessPassbook(user: WithRole | null | undefined, idPassbook?: string | null): boolean {
  if (!user || !idPassbook) return false;
  if (user.role === 'Admin' || user.role === 'Owner') return true;
  const ids = passbookIdsOf(user);
  if (ids.length === 0) return true;
  return ids.includes(String(idPassbook));
}
